import classNames from 'classnames'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { Calendar } from 'primereact/calendar'
import { InputMask } from 'primereact/inputmask'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import React from 'react'
import { Controller, FieldValues, UseFormReturn } from 'react-hook-form'
import { Company } from '../../../../../model/company.model'
import {
  getFormErrorMessage,
  getRequiredFormMessage,
} from '../../../../util/ErrorText'
import {
  getBirthDayYearRange,
  getMonthNavigatorTemplate,
  getYearNavigatorTemplate,
} from '../../../../util/form/calendar/calendar-elements'
import { CompanyRelationshipDropdown } from '../../../../util/form/dropdown/company-relationship-dropdown/CompanyRelationshipDropdown'
import CompanyDropdown from '../../../../util/form/dropdown/CompanyDropdown/CompanyDropdown'
import { EmployeeDropdown } from '../../../../util/form/dropdown/person-dropdown/EmployeeDropdown'

export const CompanyForm = (
  formMethods: UseFormReturn<FieldValues, Company>
) => {
  const getRequiredMessage = (field: string) => {
    return getRequiredFormMessage('company.' + field)
  }

  return (
    <div className={'formgrid'}>
      <div className="field grid">
        <label
          htmlFor="company_name"
          className={classNames(
            { 'p-error': formMethods.formState.errors.company_name },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.company_name')} *
        </label>
        <div className="col-12 md:col-10">
          <Controller
            name="company_name"
            control={formMethods.control}
            rules={{ required: getRequiredMessage('company_name') }}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                value={field.value || ''}
                className={classNames(
                  { 'p-invalid': fieldState.error },
                  ' w-full'
                )}
              />
            )}
          />
          {getFormErrorMessage(formMethods.formState.errors, 'company_name')}
        </div>
      </div>
      <div className="field grid">
        <label
          htmlFor="id_company"
          className={classNames(
            { 'p-error': formMethods.formState.errors.id_company },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.id_company')}
        </label>
        <div className="col-12 md:col-10">
          <CompanyDropdown fieldName={'id_company'} />
          {getFormErrorMessage(formMethods.formState.errors, 'id_company')}
        </div>
      </div>

      <div className="field grid">
        <label
          htmlFor="id_company_relationship_type"
          className={classNames(
            {
              'p-error':
                formMethods.formState.errors.id_company_relationship_type,
            },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.id_company_relationship_type')} *
        </label>
        <div className="col-12 md:col-10">
          <CompanyRelationshipDropdown />
          {getFormErrorMessage(
            formMethods.formState.errors,
            'id_company_relationship_type'
          )}
        </div>
      </div>

      <div className="field grid">
        <label
          htmlFor="email"
          className={classNames(
            { 'p-error': formMethods.formState.errors.email },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.email')} *
        </label>
        <div className="col-12 md:col-10">
          <Controller
            name="email"
            control={formMethods.control}
            rules={{ required: getRequiredMessage('email') }}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                value={field.value || ''}
                type={'email'}
                placeholder={'info@xenia-software.ch'}
                className={classNames(
                  { 'p-invalid': fieldState.error },
                  ' w-full'
                )}
              />
            )}
          />
          {getFormErrorMessage(formMethods.formState.errors, 'email')}
        </div>
      </div>

      <div className="field grid">
        <label
          htmlFor="address"
          className={classNames(
            { 'p-error': formMethods.formState.errors.address },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.address')} *
        </label>
        <div className="col-12 md:col-10">
          <Controller
            name="address"
            control={formMethods.control}
            rules={{ required: getRequiredMessage('address') }}
            render={({ field, fieldState }) => (
              <InputTextarea
                id={field.name}
                {...field}
                cols={3}
                value={field.value || ''}
                className={classNames(
                  { 'p-invalid': fieldState.error },
                  ' w-full'
                )}
              />
            )}
          />
          {getFormErrorMessage(formMethods.formState.errors, 'address')}
        </div>
      </div>
      <div className="field grid">
        <label
          htmlFor="p_o_box"
          className={classNames(
            { 'p-error': formMethods.formState.errors.p_o_box },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.p_o_box')}
        </label>
        <div className="col-12 md:col-10">
          <Controller
            name="p_o_box"
            control={formMethods.control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                value={field.value || ''}
                className={classNames(
                  { 'p-invalid': fieldState.error },
                  ' w-full'
                )}
              />
            )}
          />
          {getFormErrorMessage(formMethods.formState.errors, 'p_o_box')}
        </div>
      </div>

      <div className="field grid">
        <label
          htmlFor="uid"
          className={classNames(
            { 'p-error': formMethods.formState.errors.uid },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.uid')}
        </label>
        <div className="col-12 md:col-3">
          <Controller
            name="uid"
            control={formMethods.control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                value={field.value || ''}
                placeholder="CHE-463.750.014"
                className={classNames(
                  { 'p-invalid': fieldState.error },
                  ' w-full'
                )}
              />
            )}
          />
          {getFormErrorMessage(formMethods.formState.errors, 'uid')}
        </div>
        <label
          htmlFor="uid_addition"
          className={classNames(
            { 'p-error': formMethods.formState.errors.uid },
            'col-12 mb-2 md:col-3 md:mb-0'
          )}
        >
          {t<string>('company.uid_addition')}
        </label>
        <div className="col-12 md:col-4">
          <Controller
            name="uid_addition"
            control={formMethods.control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                value={field.value || ''}
                className={classNames(
                  { 'p-invalid': fieldState.error },
                  ' w-full'
                )}
              />
            )}
          />
          {getFormErrorMessage(formMethods.formState.errors, 'uid_addition')}
        </div>
      </div>
      <div className="field grid">
        <label
          htmlFor="vacation_days"
          className={classNames(
            { 'p-error': formMethods.formState.errors.vacation_days },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.vacation_days')}
        </label>
        <div className="col-12 md:col-10">
          <Controller
            name="vacation_days"
            control={formMethods.control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                type={'number'}
                max={365}
                value={field.value || ''}
                className={classNames(
                  { 'p-invalid': fieldState.error },
                  ' w-full'
                )}
              />
            )}
          />
          {getFormErrorMessage(formMethods.formState.errors, 'vacation_days')}
        </div>
      </div>
      <div className="field grid">
        <label
          htmlFor="phone"
          className={classNames(
            { 'p-error': formMethods.formState.errors.phone },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.phone')}
        </label>
        <div className="col-12 md:col-10">
          <Controller
            name="phone"
            control={formMethods.control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                value={field.value || ''}
                placeholder={'+41 12 345 67 89'}
                className={classNames(
                  { 'p-invalid': fieldState.error },
                  ' w-full'
                )}
              />
            )}
          />
          {getFormErrorMessage(formMethods.formState.errors, 'phone')}
        </div>
      </div>
      <div className="field grid">
        <label
          htmlFor="slack_workspace_url"
          className={classNames(
            { 'p-error': formMethods.formState.errors.phone },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.slack_workspace_url')}
        </label>
        <div className="col-12 md:col-10">
          <Controller
            name="slack_workspace_url"
            control={formMethods.control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                value={field.value || ''}
                placeholder={'https://xeniasoftware.slack.com/'}
                className={classNames(
                  { 'p-invalid': fieldState.error },
                  ' w-full'
                )}
              />
            )}
          />
          {getFormErrorMessage(
            formMethods.formState.errors,
            'slack_workspace_url'
          )}
        </div>
      </div>
      <div className="field grid">
        <label
          htmlFor="company_logo_reference"
          className={classNames(
            { 'p-error': formMethods.formState.errors.company_logo_reference },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.company_logo_reference')}
        </label>
        <div className="col-12 md:col-10">
          <Controller
            name="company_logo_reference"
            control={formMethods.control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                value={field.value || ''}
                placeholder={'https://xenia-softare.ch/logo-img.png/'}
                className={classNames(
                  { 'p-invalid': fieldState.error },
                  ' w-full'
                )}
              />
            )}
          />
          {getFormErrorMessage(
            formMethods.formState.errors,
            'company_logo_reference'
          )}
        </div>
      </div>
      <div className="field grid">
        <label
          htmlFor="bank_name"
          className={classNames(
            { 'p-error': formMethods.formState.errors.bank_name },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.bank_name')}
        </label>
        <div className="col-12 md:col-10">
          <Controller
            name="bank_name"
            control={formMethods.control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                value={field.value || ''}
                className={classNames(
                  { 'p-invalid': fieldState.error },
                  ' w-full'
                )}
              />
            )}
          />
          {getFormErrorMessage(formMethods.formState.errors, 'bank_name')}
        </div>
      </div>

      <div className="field grid">
        <label
          htmlFor="iban"
          className={classNames(
            { 'p-error': formMethods.formState.errors.iban },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.iban')}
        </label>
        <div className="col-12 md:col-10">
          <Controller
            name="iban"
            control={formMethods.control}
            render={({ field, fieldState }) => (
              <InputMask
                id={field.name}
                {...field}
                value={field.value || undefined}
                mask="aa** **** **** **** **** *"
                placeholder={'CH12 3456 7898 7654 3212 R'}
                className={classNames(
                  { 'p-invalid': fieldState.error },
                  ' w-full'
                )}
              />
            )}
          />
          {getFormErrorMessage(formMethods.formState.errors, 'iban')}
        </div>
      </div>
      <div className="field grid">
        <label
          htmlFor="founding_date"
          className={classNames(
            { 'p-error': formMethods.formState.errors.founding_date },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.founding_date')}
        </label>
        <div className="col-12 md:col-10">
          <Controller
            name="founding_date"
            control={formMethods.control}
            render={({ field, fieldState }) => (
              <Calendar
                appendTo={document.body}
                id={field.name}
                onChange={(e) => {
                  field.onChange(e.value)
                }}
                dateFormat={'dd.mm.yy'}
                placeholder="29.01.2021"
                value={field.value ? dayjs(field.value).toDate() : undefined}
                monthNavigator
                onBlur={field.onBlur}
                monthNavigatorTemplate={getMonthNavigatorTemplate}
                yearNavigator
                yearRange={getBirthDayYearRange()}
                yearNavigatorTemplate={getYearNavigatorTemplate}
                showIcon
                className={classNames(
                  { 'p-invalid': fieldState.error },
                  ' w-full'
                )}
              />
            )}
          />
          {getFormErrorMessage(formMethods.formState.errors, 'founding_date')}
        </div>
      </div>
      <div className="field grid">
        <label
          htmlFor="id_ceo"
          className={classNames(
            { 'p-error': formMethods.formState.errors.id_ceo },
            'col-12 mb-2 md:col-2 md:mb-0'
          )}
        >
          {t<string>('company.id_ceo')}
        </label>
        <div className="col-12 md:col-10">
          <EmployeeDropdown fieldName={'id_ceo'} />
          {getFormErrorMessage(formMethods.formState.errors, 'id_ceo')}
        </div>
      </div>
    </div>
  )
}